"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { NewsAPI } from "@/api/news/news.api";
import PaginationCustom from "@/common/PaginationCustom";
import { NewsCard } from "@/components/ui/NewsCard";
import { NewsInterface } from "@/types/news";
import { 
  Newspaper, 
  Megaphone, 
  Globe, 
  Calendar,
  TrendingUp,
  Users,
  BookOpen,
  Search,
  Eye,
  Lightbulb,
  Palette
} from "lucide-react";
import Link from "next/link";

const NewsSection = () => {
  const [news, setNews] = useState<NewsInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(""); // ✅ query sau khi debounce
  const [selectedCategory, setSelectedCategory] = useState("all");

  const isMountedRef = useRef(false);

  // ✅ debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrentPage(1); // reset về trang 1 khi đổi query
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      const limit = 6;

      const res = await NewsAPI.getNews({ 
        currentPage, 
        limit, 
        title: debouncedQuery || undefined  // ✅ truyền search query
      });

      if (res.status === 200) {
        const { articles, total } = res.data as {
          articles: NewsInterface[];
          total: number;
        };
        setNews(articles);
        setTotalPages(Math.ceil(total / limit));
      }
    } catch (err) {
      console.error("Failed to fetch news:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedQuery]);

  useEffect(() => {
    if (isMountedRef.current) {
      fetchNews();
    } else {
      isMountedRef.current = true;
      fetchNews();
    }
  }, [fetchNews]);

  const NewsSkeletonLoader = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  gap-4 sm:gap-6 lg:gap-8">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="animate-pulse space-y-4">
          <div 
            className="h-48 sm:h-56 lg:h-64 rounded-2xl bg-gradient-to-r bg-[length:200%_100%] animate-[shimmer_2s_infinite]"
            style={{ 
              background: `linear-gradient(90deg, var(--color-bg-secondary) 25%, var(--color-bg-accent) 50%, var(--color-bg-secondary) 75%)`,
              backgroundSize: '200% 100%'
            }}
          />
          <div className="space-y-3">
            <div 
              className="h-4 rounded-lg bg-gradient-to-r bg-[length:200%_100%] animate-[shimmer_2s_infinite]"
              style={{ 
                background: `linear-gradient(90deg, var(--color-bg-secondary) 25%, var(--color-bg-accent) 50%, var(--color-bg-secondary) 75%)`,
                backgroundSize: '200% 100%'
              }}
            />
            <div 
              className="h-4 w-3/4 rounded-lg bg-gradient-to-r bg-[length:200%_100%] animate-[shimmer_2s_infinite]"
              style={{ 
                background: `linear-gradient(90deg, var(--color-bg-secondary) 25%, var(--color-bg-accent) 50%, var(--color-bg-secondary) 75%)`,
                backgroundSize: '200% 100%'
              }}
            />
            <div 
              className="h-3 w-1/2 rounded-lg bg-gradient-to-r bg-[length:200%_100%] animate-[shimmer_2s_infinite]"
              style={{ 
                background: `linear-gradient(90deg, var(--color-bg-secondary) 25%, var(--color-bg-accent) 50%, var(--color-bg-secondary) 75%)`,
                backgroundSize: '200% 100%'
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const FeaturedNewsCard = ({ article }: { article: NewsInterface }) => (
    <Link 
      prefetch
      href={`/tin-tuc/${article.slug}`}
      className="col-span-full lg:col-span-2 rounded-3xl p-6 sm:p-8 group cursor-pointer transition-all duration-500 hover:scale-[1.02]"
      style={{ 
        backgroundColor: 'var(--color-bg)',
        boxShadow: 'var(--shadow-default)',
        border: '1px solid var(--color-border-light)'
      }}
    >
      <div className="grid lg:grid-cols-2 gap-6 h-full">
        <div className="relative overflow-hidden rounded-2xl">
          <div 
            className="w-full h-48 sm:h-64 lg:h-full rounded-2xl"
            style={{ 
              backgroundColor: 'var(--color-bg-accent)',
              backgroundImage: `url(${article.imageUrl || ''})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute top-4 left-4">
            <span 
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ 
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-text-white)'
              }}
            >
              Nổi bật
            </span>
          </div>
        </div>
        
        <div className="flex flex-col justify-center space-y-4">
          <div className="flex items-center space-x-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>{new Date(article.createdAt || '').toLocaleDateString('vi-VN')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Eye className="w-4 h-4" />
              <span>12.000 lượt xem</span>
            </div>
          </div>
          
          <h2 
            className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight group-hover:text-opacity-80 transition-all duration-300"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {article.title}
          </h2>
          
          <div className="flex items-center justify-between pt-4">
            <div className="flex items-center space-x-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
                style={{ 
                  backgroundColor: 'var(--color-primary-light)',
                  color: 'var(--color-primary)'
                }}
              >
                A
              </div>
            </div>
            
            <button 
              className="cursor-pointer inline-flex items-center space-x-2 px-4 py-2 rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105"
              style={{ 
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-text-white)'
              }}
            >
              <span>Đọc thêm</span>
              <BookOpen className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--gradient-secondary)' }}>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        
        {/* Search Section */}
        <div className="mb-6">
          <div className="relative max-w-md mx-auto sm:max-w-lg">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
            </div>
            <input
              type="text"
              placeholder="Tìm kiếm bài viết về guitar ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 sm:py-4 rounded-2xl transition-all duration-300 focus:ring-4 focus:ring-opacity-20 text-sm sm:text-base"
              style={{ 
                backgroundColor: 'var(--color-bg-secondary)',
                border: '2px solid var(--color-border)',
                color: 'var(--color-text-primary)',
              }}
            />
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <NewsSkeletonLoader />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  gap-4 sm:gap-6 lg:gap-8 mb-12">
              {news.length > 0 && <FeaturedNewsCard article={news[0]} />}
              {news.slice(1).map((item) => (
                <NewsCard key={item.id} news={item} />
              ))}
            </div>

            {news.length === 0 && !loading && (
              <div className="text-center py-12">Không có bài viết nào</div>
            )}

            {totalPages > 1 && (
              <PaginationCustom
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NewsSection;
